import { useEffect, useState, useRef } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui-helpers";
import { toast } from "sonner";
import { Quote, Plus, Trash2, Save, Pencil, X, Upload } from "lucide-react";

// Admin-only manager for the Quote of the Day list. Add / edit / enable-disable / remove.
// Disabled quotes are skipped in the daily rotation; an empty list just hides the banner.
export function QuotesManagerCard() {
  const [quotes, setQuotes] = useState(null);
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState("");
  const [editText, setEditText] = useState("");
  const [editAuthor, setEditAuthor] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  useEffect(() => { load(); }, []);

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/quotes/import", fd);
      const { added = 0, skipped = 0 } = r.data || {};
      if (added > 0) toast.success(`Imported ${added} quote${added === 1 ? "" : "s"}${skipped ? ` (${skipped} skipped)` : ""}`);
      else toast.info(`No new quotes added${skipped ? ` (${skipped} skipped — duplicates or too short)` : ""}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const add = async () => {
    if (!text.trim()) { toast.error("Enter the quote text"); return; }
    setSaving(true);
    try { await api.post("/quotes", { text, author }); setText(""); setAuthor(""); toast.success("Quote added"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const toggle = async (q) => {
    try { await api.put(`/quotes/${q.id}`, { enabled: !q.enabled }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const startEdit = (q) => { setEditId(q.id); setEditText(q.text); setEditAuthor(q.author || ""); };
  const saveEdit = async () => {
    if (!editText.trim()) { toast.error("Quote text cannot be empty"); return; }
    try { await api.put(`/quotes/${editId}`, { text: editText, author: editAuthor }); setEditId(""); toast.success("Quote updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (q) => {
    if (!window.confirm("Remove this quote from the rotation?")) return;
    try { await api.delete(`/quotes/${q.id}`); toast.success("Quote removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const enabledCount = (quotes || []).filter((q) => q.enabled).length;

  return (
    <Card className="p-5 mb-5" data-testid="quotes-manager-card">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Quote className="h-4 w-4 text-primary" />
          <h3 className="font-head font-bold text-slate-900">Quote of the Day</h3>
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="quote-import-input"
          onChange={(e) => importFile(e.target.files?.[0])} />
        <button data-testid="quote-import-btn" onClick={() => fileRef.current?.click()} disabled={importing}
          title="Import quotes from a PDF, Word (.docx) or text file"
          className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-slate-50 disabled:opacity-60">
          <Upload className="h-3.5 w-3.5" /> {importing ? "Importing…" : "Import from file"}
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">{`A daily quote greets the team on their dashboard, rotating through the enabled quotes one per day. Import a PDF, Word or text file with one quote per line (add an author after a dash, e.g. "Stay curious — Anon").${quotes ? ` ${enabledCount} of ${quotes.length} enabled.` : ""}`}</p>

      <div className="rounded-xl border border-slate-200 p-3 mb-4 space-y-2">
        <input data-testid="quote-new-text" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !saving) add(); }}
          placeholder="Quote text" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
        <div className="flex gap-2">
          <input data-testid="quote-new-author" value={author} onChange={(e) => setAuthor(e.target.value)}
            placeholder="Author (optional)" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
          <button data-testid="quote-add-btn" onClick={add} disabled={saving}
            className="inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      {quotes === null ? <div className="text-sm text-slate-400">Loading…</div>
        : quotes.length === 0 ? <div className="text-sm text-slate-400">No quotes yet — add one above.</div>
          : (
            <div className="space-y-2 max-h-[26rem] overflow-auto pr-1">
              {quotes.map((q) => (
                <div key={q.id} data-testid={`quote-row-${q.id}`} className={`rounded-xl border p-3 ${q.enabled ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-70"}`}>
                  {editId === q.id ? (
                    <div className="space-y-2">
                      <input data-testid="quote-edit-text" value={editText} onChange={(e) => setEditText(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                      <div className="flex gap-2">
                        <input data-testid="quote-edit-author" value={editAuthor} onChange={(e) => setEditAuthor(e.target.value)} placeholder="Author (optional)" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        <button data-testid="quote-edit-save" onClick={saveEdit} className="inline-flex items-center gap-1.5 bg-primary text-white px-3 py-2 rounded-lg text-xs font-semibold"><Save className="h-3.5 w-3.5" /> Save</button>
                        <button data-testid="quote-edit-cancel" onClick={() => setEditId("")} className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-600 px-3 py-2 rounded-lg text-xs font-semibold"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-800">{q.text}</p>
                        {q.author && <p className="text-xs text-slate-500 mt-0.5">— {q.author}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`quote-toggle-${q.id}`} onClick={() => toggle(q)} title={q.enabled ? "Disable" : "Enable"}
                          className={`relative h-5 w-9 rounded-full transition-colors ${q.enabled ? "bg-primary" : "bg-slate-300"}`}>
                          <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${q.enabled ? "translate-x-4" : ""}`} />
                        </button>
                        <button data-testid={`quote-edit-${q.id}`} onClick={() => startEdit(q)} className="text-slate-400 hover:text-primary p-1"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`quote-delete-${q.id}`} onClick={() => remove(q)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
    </Card>
  );
}
